import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Save, Plug, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import TimetecTerminalsCard from './TimetecTerminalsCard';

const sourceBadge = (src) => {
  if (src === 'db') return <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full text-xs">DB</span>;
  if (src === 'env') return <span className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full text-xs">.env</span>;
  return <span className="text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full text-xs">Unset</span>;
};

const IntegrationCard = ({ integration, onSaved }) => {
  const [draft, setDraft] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const init = {};
    Object.entries(integration.fields || {}).forEach(([key, f]) => {
      init[key] = f.value || '';
    });
    setDraft(init);
  }, [integration]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/integrations/${integration.key}`, { fields: draft });
      toast.success('Integration saved');
      onSaved && onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/integrations/${integration.key}/test`);
      if (r.data.success) toast.success(`Connected. ${r.data.rows_received} tip row(s) returned for the test window.`);
      else toast.error(`Test failed: ${r.data.error}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const label = integration.key === 'cash_manager' ? 'Cash Manager (Cash Recon)' : integration.key;

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-3" data-testid={`integration-card-${integration.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Plug className="w-5 h-5" /> {label}
          </div>
          <div className="text-xs text-stone-500 mt-1">
            {integration.updated_at
              ? <>Last updated {new Date(integration.updated_at).toLocaleString('en-ZA')} by <code>{integration.updated_by}</code></>
              : 'Not yet configured'}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}
          data-testid={`test-${integration.key}-button`}>
          <RefreshCw className={`w-3 h-3 mr-1 ${testing ? 'animate-spin' : ''}`} /> Test connection
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(integration.fields || {}).map(([fieldKey, f]) => (
          <div key={fieldKey}>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">{f.label}</Label>
              {sourceBadge(integration.sources?.[fieldKey])}
            </div>
            <div className="relative">
              <Input
                type={f.secret && !showSecrets[fieldKey] ? 'password' : 'text'}
                value={draft[fieldKey] ?? ''}
                onChange={e => setDraft({ ...draft, [fieldKey]: e.target.value })}
                placeholder={f.is_set && f.value && String(f.value).includes('•') ? f.value : ''}
                data-testid={`integration-${integration.key}-${fieldKey}-input`}
              />
              {f.secret && (
                <button type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, [fieldKey]: !showSecrets[fieldKey] })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
                  {showSecrets[fieldKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`save-${integration.key}-button`}>
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {integration.key === 'cash_manager' && (
        <div className="bg-stone-50 border border-stone-200 rounded p-3 text-xs text-stone-600 space-y-1">
          <div className="font-medium text-stone-800 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Setup checklist</div>
          <div>1. Paste the Base URL (e.g. <code>https://cash.brotherscoffee.co.za</code>) and the API Key.</div>
          <div>2. Enter the exact business name so the integration only pulls cashups for this business.</div>
          <div>3. Map each branch in <b>Settings → Branches</b> using <i>Cash Manager Branch Name</i> when the name differs from your local branch name.</div>
          <div>4. Flag tip-eligible staff in <b>Settings → Users</b> using the <i>Include in Gratuities</i> toggle.</div>
        </div>
      )}
    </div>
  );
};


export function IntegrationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/integrations`);
      setItems(r.data.integrations || []);
    } catch (e) { toast.error('Failed to load integrations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaved = (updated) => {
    setItems(prev => prev.map(it => it.key === updated.key ? updated : it));
  };

  return (
    <div className="space-y-4" data-testid="integrations-tab">
      <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">API Integrations</h2>
          <p className="text-xs text-stone-500 mt-1">Master Admin only. Secrets are encrypted at rest and masked in API responses.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {items.map(it => (
        <IntegrationCard key={it.key} integration={it} onSaved={handleSaved} />
      ))}

      <TimetecTerminalsCard />
    </div>
  );
}

export default IntegrationsTab;
